import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { firestore } from '../firebase';
import { fetchGame } from '../util/api/game';

/**
 * Custom hook for managing user games and bookmark folders
 * Extracted from profile.js for reuse in mobile and web components
 */
export const useUserGames = (userHandle, authenticatedUserId, editProfile = false) => {
  const [games, setGames] = useState([]);
  const [folders, setFolders] = useState([]);
  const [nonEmptyFolders, setNonEmptyFolders] = useState([]);
  const [currentFolderGames, setCurrentFolderGames] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch games for a specific user
  const fetchGamesForUser = useCallback(async (gameLimit = 20, handle = "") => {
    try {
      setIsLoadingGames(true);

      if (!handle) {
        setGames([]);
        setIsLoadingGames(false);
        return;
      }

      let filteredGames = [];
      
      try {
        // First attempt: server-side filtering
        const gamesQuery = query(
          collection(firestore, "games"),
          where("creator", "==", handle),
          orderBy("createdAt", "desc"),
          limit(gameLimit)
        );

        const gamesSnapshot = await getDocs(gamesQuery);
        
        gamesSnapshot.forEach(doc => {
          filteredGames.push({
            id: doc.id,
            ...doc.data()
          });
        });
      } catch (serverError) {
        console.warn("Server-side filtering failed, falling back to client-side:", serverError);
        
        // Fall back to client-side filtering with limited results
        const gamesQuery = query(
          collection(firestore, "games"),
          orderBy("createdAt", "desc"),
          limit(gameLimit * 5)
        );

        const gamesSnapshot = await getDocs(gamesQuery);
        const allGames = [];

        gamesSnapshot.forEach(doc => {
          allGames.push({
            id: doc.id,
            ...doc.data()
          });
        });

        // Filter games client-side with case-insensitive matching
        const normalizedHandle = handle.toLowerCase();
        filteredGames = allGames
          .filter(game => 
            game.creator && 
            game.creator.toLowerCase() === normalizedHandle
          )
          .slice(0, gameLimit);
      }

      setGames(filteredGames);
      setIsLoadingGames(false);
    } catch (error) {
      console.error("Error querying games:", error);
      setGames([]);
      setIsLoadingGames(false);
    }
  }, []);

  // Fetch bookmark folders for user
  const fetchUserFolders = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // For viewing own profile, use userId. For others, we need to get their userId first
      let targetUserIdForBookmarks = authenticatedUserId;
      
      if (!editProfile && userHandle) {
        try {
          const userQuery = query(
            collection(firestore, "users"),
            where("handle", "==", userHandle),
            limit(1)
          );
          const userSnapshot = await getDocs(userQuery);
          
          if (!userSnapshot.empty) {
            targetUserIdForBookmarks = userSnapshot.docs[0].data().userId;
          } else {
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error("Error fetching user for bookmarks:", error);
          setIsLoading(false);
          return;
        }
      }

      if (!targetUserIdForBookmarks) {
        setIsLoading(false);
        return;
      }

      // Fetch bookmark folders with limit
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", targetUserIdForBookmarks),
        where("isPrivate", "==", false),
        limit(50)
      );

      const folderSnapshot = await getDocs(folderQuery);

      let fetchedFolders = [];

      if (!folderSnapshot.empty) {
        fetchedFolders = folderSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          folderIndex: doc.data().folderIndex !== undefined ? doc.data().folderIndex : 999
        }));
      }

      // Ensure default folders exist
      let mergedFolders = [...fetchedFolders];

      // Check if Featured folder exists, if not add it
      if (!fetchedFolders.some(folder => folder.folderName === 'Featured')) {
        mergedFolders.push({
          folderName: 'Featured',
          isPrivate: false,
          games: [],
          userId: targetUserIdForBookmarks,
          folderIndex: 0
        });
      } else {
        // Ensure Featured has index 0
        mergedFolders = mergedFolders.map(folder =>
          folder.folderName === 'Featured' ? { ...folder, folderIndex: 0 } : folder
        );
      }

      // Sort folders by folderIndex within their privacy category
      mergedFolders.sort((a, b) => {
        if (a.isPrivate !== b.isPrivate) {
          return a.isPrivate ? 1 : -1;
        }
        return a.folderIndex - b.folderIndex;
      });

      setFolders(mergedFolders);

      // Filter folders for rendering - only show folders with games for all profiles
      const foldersWithGames = mergedFolders.filter(folder => {
        if (!editProfile) {
          return !folder.isPrivate && folder.games && Array.isArray(folder.games) && folder.games.length > 0;
        }
        return folder.games && Array.isArray(folder.games) && folder.games.length > 0;
      });

      setNonEmptyFolders(foldersWithGames);

      // Fetch game details for all bookmarked games
      const gameIds = new Set();
      mergedFolders.forEach(folder => {
        if (folder.games && Array.isArray(folder.games)) {
          folder.games.forEach(game => {
            gameIds.add(game.gameId);
          });
        }
      });

      // Only fetch game details if we have a reasonable number
      if (gameIds.size > 0 && gameIds.size <= 100) {
        await fetchBatchData(Array.from(gameIds));
      } else if (gameIds.size > 100) {
        await fetchBatchData(Array.from(gameIds).slice(0, 100));
      }

      setIsLoading(false);
    } catch (error) {
      setError('Failed to load bookmarks. Please try again.');
      setIsLoading(false);
    }
  }, [userHandle, authenticatedUserId, editProfile]);

  // Fetch batch game data
  const fetchBatchData = useCallback(async (gameIds, folderName = null) => {
    if (!gameIds || gameIds.length === 0) return;

    try {
      const batchSize = 10;
      const batches = [];
      
      for (let i = 0; i < gameIds.length; i += batchSize) {
        batches.push(gameIds.slice(i, i + batchSize));
      }

      const allGames = [];
      
      for (const batch of batches) {
        const gamePromises = batch.map(gameId => fetchGame(gameId));
        const gameResults = await Promise.allSettled(gamePromises);
        
        const batchGames = gameResults
          .filter(result => result.status === 'fulfilled')
          .map(result => result.value);
          
        allGames.push(...batchGames);
        
        if (batches.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      if (folderName) {
        setCurrentFolderGames(allGames);
      } else {
        setGamesData(allGames);
      }
    } catch (error) {
      console.error('Error fetching game data:', error);
    }
  }, []);

  // Handle tab change for folder selection
  const handleTabChange = useCallback((tabName) => {
    if (tabName === 'ALL GAMES') {
      setCurrentFolderGames([]);
      return;
    }

    const selectedFolder = folders.find(folder => folder.folderName === tabName);

    if (selectedFolder && selectedFolder.games && selectedFolder.games.length > 0) {
      const gameIds = selectedFolder.games.map(game => game.gameId);

      if (gameIds.length > 0) {
        fetchBatchData(gameIds, tabName);
      } else {
        setCurrentFolderGames([]);
      }
    } else {
      setCurrentFolderGames([]);
    }
  }, [folders, fetchBatchData]);

  // Fetch games when user handle changes
  useEffect(() => {
    if (userHandle) {
      fetchGamesForUser(20, userHandle);
    }
  }, [userHandle, fetchGamesForUser]);

  // Fetch folders when dependencies change
  useEffect(() => {
    if (userHandle) {
      fetchUserFolders();
    }
  }, [fetchUserFolders]);

  // Helper to get valid games
  const validGames = games.filter(game => game && Object.hasOwn(game, "id"));

  return {
    // Games data
    games: validGames,
    folders,
    nonEmptyFolders,
    currentFolderGames,
    gamesData,
    
    // Loading states
    isLoadingGames,
    isLoading,
    error,
    
    // Functions
    fetchGamesForUser,
    fetchUserFolders,
    fetchBatchData,
    handleTabChange,
    
    // Helper
    postsCount: validGames.length
  };
};